import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getSaved } from "@/lib/storage";
import { CATEGORIES } from "@/lib/catalog";
import { useStackMode } from "@/lib/stackModeContext";
import { Activity, Layers, ShieldAlert, Clock, ChevronRight, ArrowLeft, Gauge } from "lucide-react";

// Best-effort numeric "MW" extraction from a saved entry's inputs
function findInputValue(it, keyRegex) {
  for (const [k, v] of Object.entries(it.inputs || {})) {
    if (keyRegex.test(k)) {
      const n = Number(String(v).replace(/[^\d.\-]/g, ""));
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

export default function WellDashboard() {
  const { wellName } = useParams();
  const decoded = decodeURIComponent(wellName || "");
  const navigate = useNavigate();
  const all = getSaved();
  const { stackMode: globalStack } = useStackMode();
  const [stackFilter, setStackFilter] = useState(globalStack); // all | surface | subsea (default = global)
  useEffect(() => { setStackFilter(globalStack); }, [globalStack]);
  const items = useMemo(() => {
    const list = all.filter((x) => x.wellName === decoded);
    if (stackFilter === "all") return list;
    return list.filter((x) => (x.stackMode || (x.category === "wellcontrol" ? "surface" : null)) === stackFilter);
  }, [all, decoded, stackFilter]);

  const kpis = useMemo(() => {
    let maxMW = -Infinity, maxTVD = -Infinity, last = null, worksheets = 0;
    const byCategory = {};
    items.forEach((it) => {
      byCategory[it.category] = (byCategory[it.category] || 0) + 1;
      if (it.worksheetWarning) worksheets++;
      if (!last || new Date(it.createdAt) > new Date(last.createdAt)) last = it;
      const mw = findInputValue(it, /mud weight|^mw\b/i);
      if (mw != null && mw > maxMW) maxMW = mw;
      const tvd = findInputValue(it, /\bTVD\b/i);
      if (tvd != null && tvd > maxTVD) maxTVD = tvd;
    });
    return {
      total: items.length,
      maxMW: maxMW === -Infinity ? null : maxMW,
      maxTVD: maxTVD === -Infinity ? null : maxTVD,
      worksheets,
      lastEntry: last,
      byCategory,
    };
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="flex flex-col gap-4" data-testid="well-dashboard-empty">
        <button onClick={() => navigate(-1)} className="btn-ghost h-9 px-3 rounded-md inline-flex items-center gap-2 text-xs label-up w-fit">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <section>
          <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)]">Well</div>
          <h2 className="font-heading font-black text-3xl tracking-tight mt-1">{decoded || "—"}</h2>
        </section>
        <StackFilter value={stackFilter} onChange={setStackFilter} />
        <div className="card-rs p-6 text-center text-sm text-[var(--rs-text-2)]">
          No saved calculations found for this well{stackFilter !== "all" ? ` (filter: ${stackFilter})` : ""}.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5" data-testid="well-dashboard">
      <section>
        <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)]">Well Dashboard</div>
        <h2 className="font-heading font-black text-3xl tracking-tight mt-1">{decoded}</h2>
        <p className="text-sm text-[var(--rs-text-2)] mt-1.5">All saved calculations for this well, with quick operational KPIs.</p>
      </section>

      <StackFilter value={stackFilter} onChange={setStackFilter} />

      <div className="grid grid-cols-2 gap-3" data-testid="well-kpis">
        <Kpi Icon={Layers} label="Total calcs" value={kpis.total} testid="kpi-total" />
        <Kpi Icon={ShieldAlert} label="Worksheets" value={kpis.worksheets} testid="kpi-worksheets" />
        <Kpi Icon={Gauge} label="Max MW (ppg)" value={kpis.maxMW != null ? kpis.maxMW : "—"} testid="kpi-mw" />
        <Kpi Icon={Activity} label="Max TVD (ft)" value={kpis.maxTVD != null ? kpis.maxTVD : "—"} testid="kpi-tvd" />
      </div>

      {kpis.lastEntry && (
        <div className="card-rs p-3 flex items-center gap-3" data-testid="kpi-last">
          <Clock className="w-4 h-4 text-[var(--rs-amber)] shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] label-up text-[var(--rs-text-3)]">Most recent</div>
            <div className="text-sm font-semibold truncate">{kpis.lastEntry.calcName}</div>
            <div className="text-[11px] text-[var(--rs-text-3)]">{new Date(kpis.lastEntry.createdAt).toLocaleString()}</div>
          </div>
          <div className="font-mono-rs text-sm text-[var(--rs-amber)] ml-auto">{String(kpis.lastEntry.result)} <span className="text-[var(--rs-text-3)]">{kpis.lastEntry.unit}</span></div>
        </div>
      )}

      <section>
        <div className="label-up text-[var(--rs-text-3)] mb-2">Calculations ({items.length})</div>
        <div className="flex flex-col gap-2" data-testid="well-entries">
          {items.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((it) => (
            <div key={it.id} className="card-rs p-3" data-testid={`well-row-${it.id}`}>
              <div className="flex items-center justify-between">
                <div className="min-w-0 pr-3">
                  <div className="font-semibold text-sm truncate">{it.calcName}</div>
                  <div className="text-[11px] text-[var(--rs-text-3)] mt-0.5">
                    {(CATEGORIES.find((c) => c.id === it.category) || {}).name || it.category}
                    {it.rigName ? ` · ${it.rigName}` : ""}
                    {" · "}{new Date(it.createdAt).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-mono-rs text-sm text-[var(--rs-amber)]">{String(it.result)} <span className="text-[var(--rs-text-3)]">{it.unit}</span></span>
                  <button onClick={() => navigate(`/calc/${it.calcId}`)} className="btn-ghost h-8 w-8 rounded-md inline-flex items-center justify-center" aria-label="Open calculator">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Kpi({ Icon, label, value, testid }) {
  return (
    <div className="card-rs p-3" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="w-3.5 h-3.5 text-[var(--rs-amber)]" />
        <div className="label-up text-[10px] text-[var(--rs-text-3)]">{label}</div>
      </div>
      <div className="font-mono-rs text-2xl font-bold tracking-tight">{value}</div>
    </div>
  );
}

function StackFilter({ value, onChange }) {
  const opts = [
    { id: "all", label: "All" },
    { id: "surface", label: "Surface Stack" },
    { id: "subsea", label: "Subsea Stack" },
  ];
  return (
    <div className="inline-flex rounded-md border self-start" style={{ borderColor: "var(--rs-border)" }} data-testid="well-stack-filter">
      {opts.map((o, idx) => (
        <button
          key={o.id}
          data-testid={`well-stack-${o.id}`}
          onClick={() => onChange(o.id)}
          data-active={value === o.id}
          className={`unit-pill rounded-none border-0 ${idx === 0 ? "rounded-l-md" : ""} ${idx === opts.length - 1 ? "rounded-r-md" : ""}`}
          style={idx < opts.length - 1 ? { borderRight: "1px solid var(--rs-border)" } : undefined}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
